import { useState, useEffect } from 'react';
import Button from '../common/Button';

const BoardEditor = ({ board, gymId, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    type: 'moon',
    description: '',
    locationNotes: '',

    // Board Attributes
    isAdjustableAngle: false,
    angle: '',
    minAngle: '',
    maxAngle: '',
    holdSets: [],
    newHoldSet: '',
    features: {
      led: false,
      fan: false,
      mirrored: false
    },

    // Visibility & Promotion
    status: 'active',
    isFeatured: false,
    newBadgeEnabled: true,
    newBadgeExpiresAt: '',

    // Reservation Settings
    checkInGracePeriod: 10,
    weeklyHourLimit: '',
    monthlyHourLimit: '',
    maxReservationDuration: 120,

    // Advanced Settings
    overrideAdvanceBooking: false,
    maxAdvanceBookingDays: '',
    minAdvanceBookingMinutes: '',
    overrideCancellation: false,
    cancellationDeadlineMinutes: ''
  });

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (board) {
      setFormData({
        name: board.name || '',
        type: board.type || 'moon',
        description: board.description || '',
        locationNotes: board.location_notes || '',

        isAdjustableAngle: board.is_adjustable_angle || false,
        angle: board.angle !== undefined && board.angle !== null ? board.angle : '',
        minAngle: board.min_angle !== undefined && board.min_angle !== null ? board.min_angle : '',
        maxAngle: board.max_angle !== undefined && board.max_angle !== null ? board.max_angle : '',
        holdSets: board.hold_sets || [],
        newHoldSet: '',
        features: board.features || { led: false, fan: false, mirrored: false },

        status: board.status || 'active',
        isFeatured: board.is_featured || false,
        newBadgeEnabled: board.new_badge_enabled !== undefined ? board.new_badge_enabled : true,
        newBadgeExpiresAt: board.new_badge_expires_at ? board.new_badge_expires_at.split('T')[0] : '',

        checkInGracePeriod: board.check_in_grace_period_minutes !== undefined && board.check_in_grace_period_minutes !== null ? board.check_in_grace_period_minutes : 10,
        weeklyHourLimit: board.weekly_hour_limit !== undefined && board.weekly_hour_limit !== null ? board.weekly_hour_limit : '',
        monthlyHourLimit: board.monthly_hour_limit !== undefined && board.monthly_hour_limit !== null ? board.monthly_hour_limit : '',
        maxReservationDuration: board.max_reservation_duration !== undefined && board.max_reservation_duration !== null ? board.max_reservation_duration : 120,

        overrideAdvanceBooking: board.override_advance_booking || false,
        maxAdvanceBookingDays: board.max_advance_booking_days !== undefined && board.max_advance_booking_days !== null ? board.max_advance_booking_days : '',
        minAdvanceBookingMinutes: board.min_advance_booking_minutes !== undefined && board.min_advance_booking_minutes !== null ? board.min_advance_booking_minutes : '',
        overrideCancellation: board.override_cancellation || false,
        cancellationDeadlineMinutes: board.cancellation_deadline_minutes !== undefined && board.cancellation_deadline_minutes !== null ? board.cancellation_deadline_minutes : ''
      });
    }
  }, [board]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Specialized handler for numeric fields that can be 0
  const handleNumericChange = (field, value) => {
    // Convert to number if not empty, otherwise leave as empty string
    const numValue = value === '' ? '' : Number(value);
    setFormData(prev => ({ ...prev, [field]: numValue }));
  };

  const handleFeatureChange = (feature, value) => {
    setFormData(prev => ({
      ...prev,
      features: { ...prev.features, [feature]: value }
    }));
  };

  const handleAddHoldSet = () => {
    if (formData.newHoldSet.trim()) {
      setFormData(prev => ({
        ...prev,
        holdSets: [...prev.holdSets, prev.newHoldSet.trim()],
        newHoldSet: ''
      }));
    }
  };

  const handleRemoveHoldSet = (index) => {
    setFormData(prev => ({
      ...prev,
      holdSets: prev.holdSets.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    let payload = null; // Define outside try block for error logging

    try {
      // Validate angle configuration
      if (formData.isAdjustableAngle) {
        // Check if values are missing (empty string, null, or undefined) - 0 is valid!
        const minIsEmpty = formData.minAngle === '' || formData.minAngle === null || formData.minAngle === undefined;
        const maxIsEmpty = formData.maxAngle === '' || formData.maxAngle === null || formData.maxAngle === undefined;

        if (minIsEmpty || maxIsEmpty) {
          throw new Error('Adjustable boards must have both min and max angles');
        }

        const minAngleNum = typeof formData.minAngle === 'number' ? formData.minAngle : parseInt(formData.minAngle);
        const maxAngleNum = typeof formData.maxAngle === 'number' ? formData.maxAngle : parseInt(formData.maxAngle);

        if (minAngleNum > maxAngleNum) {
          throw new Error('Min angle must be less than or equal to max angle');
        }
      } else {
        const angleIsEmpty = formData.angle === '' || formData.angle === null || formData.angle === undefined;
        if (angleIsEmpty) {
          throw new Error('Fixed angle boards must have an angle specified');
        }
      }

      // Helper function to safely convert to integer or null
      const toIntOrNull = (value) => {
        if (value === '' || value === null || value === undefined) return null;
        return typeof value === 'number' ? value : parseInt(value, 10);
      };

      payload = {
        gymId: parseInt(gymId),
        name: formData.name,
        type: formData.type,
        description: formData.description || null,
        locationNotes: formData.locationNotes || null,

        isAdjustableAngle: formData.isAdjustableAngle,
        angle: formData.isAdjustableAngle ? null : toIntOrNull(formData.angle),
        minAngle: formData.isAdjustableAngle ? toIntOrNull(formData.minAngle) : null,
        maxAngle: formData.isAdjustableAngle ? toIntOrNull(formData.maxAngle) : null,
        holdSets: formData.holdSets.length > 0 ? formData.holdSets : null,
        features: formData.features,

        status: formData.status,
        isFeatured: formData.isFeatured,
        newBadgeEnabled: formData.newBadgeEnabled,
        newBadgeExpiresAt: formData.newBadgeExpiresAt || null,

        checkInGracePeriod: toIntOrNull(formData.checkInGracePeriod),
        weeklyHourLimit: toIntOrNull(formData.weeklyHourLimit),
        monthlyHourLimit: toIntOrNull(formData.monthlyHourLimit),
        maxReservationDuration: toIntOrNull(formData.maxReservationDuration),

        overrideAdvanceBooking: formData.overrideAdvanceBooking,
        maxAdvanceBookingDays: toIntOrNull(formData.maxAdvanceBookingDays),
        minAdvanceBookingMinutes: toIntOrNull(formData.minAdvanceBookingMinutes),
        overrideCancellation: formData.overrideCancellation,
        cancellationDeadlineMinutes: toIntOrNull(formData.cancellationDeadlineMinutes)
      };

      const url = board
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/boards/${board.id}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/boards`;

      const response = await fetch(url, {
        method: board ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Backend error response:', errorData);

        // Handle validation errors from express-validator
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const errorMessages = errorData.errors.map(err => `${err.param}: ${err.msg}`).join(', ');
          throw new Error(errorMessages);
        }

        throw new Error(errorData.error || 'Failed to save board');
      }

      const savedBoard = await response.json();
      onSave(savedBoard);
    } catch (err) {
      console.error('Error saving board:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 my-8">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {board ? 'Edit Board' : 'Add New Board'}
          </h2>
        </div>

        {/* Error Alert - Sticky at top of scrollable area */}
        {error && (
          <div className="sticky top-0 z-10 mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
              <button
                type="button"
                onClick={() => setError(null)}
                className="ml-3 flex-shrink-0 text-red-400 hover:text-red-600"
              >
                <span className="sr-only">Dismiss</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">

          {/* Section A: Basic Information */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Board Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Moonboard Main"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Board Type <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="moon">Moonboard</option>
                  <option value="tension">Tension Board</option>
                  <option value="kilter">Kilter Board</option>
                  <option value="decoy">Decoy Board</option>
                  <option value="grasshopper">Grasshopper Board</option>
                  <option value="spray">Spray Wall</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of this board..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location Notes
                </label>
                <input
                  type="text"
                  value={formData.locationNotes}
                  onChange={(e) => handleChange('locationNotes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 2nd floor, near entrance"
                />
              </div>
            </div>
          </div>

          {/* Section B: Board Attributes */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Board Attributes</h3>

            {/* Angle Configuration */}
            <div className="mb-4">
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={formData.isAdjustableAngle}
                  onChange={(e) => handleChange('isAdjustableAngle', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Adjustable Angle Board
                </span>
              </label>

              {formData.isAdjustableAngle ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Min Angle (°) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required={formData.isAdjustableAngle}
                      min="0"
                      max="90"
                      value={formData.minAngle}
                      onChange={(e) => handleNumericChange('minAngle', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Angle (°) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required={formData.isAdjustableAngle}
                      min="0"
                      max="90"
                      value={formData.maxAngle}
                      onChange={(e) => handleNumericChange('maxAngle', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fixed Angle (°) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required={!formData.isAdjustableAngle}
                    min="0"
                    max="90"
                    value={formData.angle}
                    onChange={(e) => handleNumericChange('angle', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}
            </div>

            {/* Features */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Features
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.features.led}
                    onChange={(e) => handleFeatureChange('led', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">LED System</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.features.fan}
                    onChange={(e) => handleFeatureChange('fan', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Fan</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.features.mirrored}
                    onChange={(e) => handleFeatureChange('mirrored', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Mirrored Layout</span>
                </label>
              </div>
            </div>

            {/* Hold Sets */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hold Sets
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={formData.newHoldSet}
                  onChange={(e) => handleChange('newHoldSet', e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddHoldSet();
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 2016 Moonboard"
                />
                <Button type="button" onClick={handleAddHoldSet} variant="outline">
                  Add
                </Button>
              </div>
              {formData.holdSets.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.holdSets.map((set, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                    >
                      {set}
                      <button
                        type="button"
                        onClick={() => handleRemoveHoldSet(index)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Section C: Visibility & Promotion */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Visibility & Promotion</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isFeatured}
                    onChange={(e) => handleChange('isFeatured', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Featured Board (pin to top)
                  </span>
                </label>
              </div>

              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.newBadgeEnabled}
                    onChange={(e) => handleChange('newBadgeEnabled', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Show "NEW" Badge
                  </span>
                </label>
              </div>

              {formData.newBadgeEnabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Badge Expires On
                  </label>
                  <input
                    type="date"
                    value={formData.newBadgeExpiresAt}
                    onChange={(e) => handleChange('newBadgeExpiresAt', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty for auto-expiration (30 days)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Section D: Reservation Policies */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reservation Policies</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Check-in Grace Period (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  max="30"
                  value={formData.checkInGracePeriod}
                  onChange={(e) => handleNumericChange('checkInGracePeriod', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  0-30 minutes or leave empty for no check-in required
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Weekly Hour Limit
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.weeklyHourLimit}
                  onChange={(e) => handleNumericChange('weeklyHourLimit', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Leave empty for no limit"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Hour Limit
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.monthlyHourLimit}
                  onChange={(e) => handleNumericChange('monthlyHourLimit', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Leave empty for no limit"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Reservation Duration (minutes)
                </label>
                <input
                  type="number"
                  min="15"
                  max="240"
                  step="15"
                  value={formData.maxReservationDuration}
                  onChange={(e) => handleNumericChange('maxReservationDuration', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum total time users can reserve in a single booking (default: 120 min = 2 hours)
                </p>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="mt-6">
              <h4 className="text-md font-medium text-gray-900 mb-3">Advanced Overrides</h4>

              <div className="space-y-4">
                <div>
                  <label className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      checked={formData.overrideAdvanceBooking}
                      onChange={(e) => handleChange('overrideAdvanceBooking', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Use Custom Booking Window
                    </span>
                  </label>
                  {formData.overrideAdvanceBooking && (
                    <div className="grid grid-cols-2 gap-4 ml-6">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          Book up to (days in advance)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={formData.maxAdvanceBookingDays}
                          onChange={(e) => handleNumericChange('maxAdvanceBookingDays', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., 7"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          Require booking (minutes in advance)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="1440"
                          value={formData.minAdvanceBookingMinutes}
                          onChange={(e) => handleNumericChange('minAdvanceBookingMinutes', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., 30"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      checked={formData.overrideCancellation}
                      onChange={(e) => handleChange('overrideCancellation', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Override Cancellation Policy
                    </span>
                  </label>
                  {formData.overrideCancellation && (
                    <div className="ml-6">
                      <label className="block text-sm text-gray-700 mb-1">
                        Cancellation Deadline (minutes before)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="1440"
                        value={formData.cancellationDeadlineMinutes}
                        onChange={(e) => handleNumericChange('cancellationDeadlineMinutes', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : (board ? 'Update Board' : 'Create Board')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BoardEditor;
